import { useRef, useState } from "react";
import { parseOffset } from "#lib/pagination";
import { ElementType } from "#lib/types";
import { fetchProfilePosts } from "#api/profiles";
import { Paginator } from "#components/pagination";
import { CardList, PostCard } from "#components/cards";
import { ButtonSubmit, FormRouter } from "#components/forms";
import { IPost } from "#entities/posts";
import { findFavoritePosts } from "#entities/account";
import { LoaderFunctionArgs, useLoaderData, useOutletContext } from "react-router";
import { createProfilePageURL } from "#lib/urls";
import { OutletContext } from "./creator-page";

interface IProps {
  query?: string;
  posts: IPost[];
  tags: string[];
  offset?: number;
  service: string;
  creatorId: string;
}

export function CreatorPostsPage() {
  const { query, posts, tags, offset, service, creatorId }: IProps = useLoaderData();
  const { creator: { post_count: postCount } }: OutletContext = useOutletContext();
  const [isLoading, setIsLoading] = useState(false);

  return (
    <>
      <div className="paginator" id="paginator-top">
        {!(posts && (postCount !== 0 || query)) ? undefined : (
          <>
            <SearchForm
              query={query}
              onLoadingChange={(loading) => setIsLoading(loading)}
            />
            <Paginator
              offset={offset}
              count={postCount}
              constructURL={(offset) =>
                String(
                  createProfilePageURL({
                    service,
                    creatorId,
                    offset,
                    query,
                    tags,
                  })
                )
              }
            />
          </>
        )}
      </div>

      {!posts.length ? (
        <div className="no-results">
          <h2 className="site-section__subheading">
            Nobody here but us chickens!
          </h2>
          <p className="subtitle">There are no posts for your query.</p>
        </div>
      ) : (
        <>
          <CardList className={isLoading ? "card-list--loading" : ""}>
            {posts.map((post) => (
              <PostCard
                key={`${post.id}-${post.service}-${post.user}`}
                post={post}
                isServiceIconsDisabled={true}
                isFavorite={post.isFavorite}
              />
            ))}
          </CardList>

          <div className="paginator" id="paginator-bottom">
            <Paginator
              offset={offset}
              count={postCount}
              constructURL={(offset) =>
                String(
                  createProfilePageURL({
                    service,
                    creatorId,
                    offset,
                    query,
                    tags,
                  })
                )
              }
            />
          </div>
        </>
      )}
    </>
  );
}

interface ISearchFormProps extends Pick<IProps, "query"> {
  onLoadingChange: (loading: boolean) => void;
}

function SearchForm({ query, onLoadingChange }: ISearchFormProps) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const target = e.currentTarget as HTMLInputElement;

    onLoadingChange(true);

    timeoutRef.current = setTimeout(() => {
      if (target.form) target.form.requestSubmit();
      onLoadingChange(false);
    }, 1000);
  };

  return (
    <FormRouter
      id="search-form"
      className="search-form"
      method="GET"
      autoComplete="off"
      noValidate={true}
      acceptCharset="UTF-8"
      statusSection={null}
      submitButton={undefined}
      style={{ maxWidth: "fit-content" }}
    >
      {(state) => (
        <>
          <div className="wrapper">
            <input
              type="text"
              name="q"
              id="q"
              autoComplete="off"
              defaultValue={query}
              placeholder="Search..."
              onChange={onInputChange}
            />
            <ButtonSubmit
              disabled={state === "loading" || state === "submitting"}
              className="search-button"
              onClick={() => {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                onLoadingChange(false);
              }}
            >
              <img src='/static/menu/search.svg' />
            </ButtonSubmit>
          </div>
        </>
      )}
    </FormRouter>
  );
}

export async function loader({
  params,
  request,
}: LoaderFunctionArgs): Promise<IProps | Response> {
  const searchParams = new URL(request.url).searchParams;
  const service = params.service?.trim() || "";
  const creatorId = params.creatorId?.trim() || "";

  const offsetParam = searchParams.get("o")?.trim();
  const offset = offsetParam ? parseOffset(offsetParam) : undefined;
  const query = searchParams.get("q")?.trim();
  const tags = searchParams.getAll("tag");

  const posts = await fetchProfilePosts(
    service,
    creatorId,
    offset,
    query,
    tags,
  );
  const favPostData = await findFavoritePosts(
    posts.map(({ service, user, id }) => {
      return {
        service,
        user,
        id,
      };
    })
  );
  const finalPosts = posts.map<
    ElementType<Required<IProps>["posts"]>
  >((post) => {
    const match = favPostData.find(
      ({ service, user, id }) =>
        service === post.service && user === post.user && id === post.id
    );

    return { ...post, isFavorite: !!match };
  });


  return {
    query,
    tags,
    offset,
    posts: finalPosts,
    service,
    creatorId,
  };
}
